import { Component, inject, signal, computed } from '@angular/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { 
  PericalReportOptions, 
  PericalReportPresets,
  TemperatureScope,
  PressureScope,
  AnnularResultsScope,
  OperationEnvelopeScope,
  OperationEnvelopeTableOutput,
  TemperatureChartConfig,
  PressureChartConfig,
  AnnularResultsChartConfig,
  OperationEnvelopeChartConfig,
  DEFAULT_CHART_CONFIGS,
  validateReportOptions
} from '../../models/perical-report-options.model';

interface PresetOption {
  label: string;
  value: string;
  options: PericalReportOptions;
}

type PericalDialogOptions = PericalReportOptions & {
  temperatureChartConfig: TemperatureChartConfig;
  pressureChartConfig: PressureChartConfig;
  annularResultsChartConfig: AnnularResultsChartConfig;
  operationEnvelopeChartConfig: OperationEnvelopeChartConfig;
};

@Component({
  selector: 'app-perical-report-options-dialog',
  template: `
  <div class="flex flex-col">
    <p-scrollPanel [style]="{ width: '100%', height: '100%' }">
      <div class="flex flex-col space-y-4 max-h-[70vh]">
        <div class="mb-2">
          <label class="block">Quick Presets</label>
          <p-select
            class="w-full"
            [options]="presets"
            [(ngModel)]="selectedPreset"
            optionLabel="label"
            placeholder="Choose a preset"
            (onChange)="applyPreset($event.value)"
          ></p-select>
        </div>

        <p-divider></p-divider>

        <div class="space-y-4">
          <div>
            <h3 class="underline pb-3">Temperature</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeTemperaturePlot" (ngModelChange)="onOptionChange('includeTemperaturePlot', $event)" [binary]="true" inputId="tempPlot" />
                <label for="tempPlot" class="ml-2">Temperature Plot</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeTemperatureTable" (ngModelChange)="onOptionChange('includeTemperatureTable', $event)" [binary]="true" inputId="tempTable" />
                <label for="tempTable" class="ml-2">Temperature Table</label>
              </div>
              <div [class.opacity-60]="!options().includeTemperaturePlot" [class.pointer-events-none]="!options().includeTemperaturePlot" class="flex items-center">
                <p-checkbox [ngModel]="options().includeFormations" (ngModelChange)="onOptionChange('includeFormations', $event)" [binary]="true" inputId="formations" />
                <label for="formations" class="ml-2">Include Formations</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeFormationColumnsInTemperatureTable" (ngModelChange)="onOptionChange('includeFormationColumnsInTemperatureTable', $event)" [binary]="true" inputId="formationColsInTable" />
                <label for="formationColsInTable" class="ml-2">Formations Table</label>
              </div>
            </div>
            @if (options().includeTemperaturePlot || options().includeTemperatureTable) {
              <div class="mt-3 ml-4 space-y-2">
                <div>
                  <label class="block mb-1 text-sm font-medium">Scope</label>
                  <p-select
                    class="w-full"
                    [options]="temperatureScopeOptions"
                    [(ngModel)]="options().temperatureChartConfig.scope"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                  ></p-select>
                </div>
                @if (options().temperatureChartConfig.scope === 'MultipleOperations') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Annulus/Pipe Names (optional)</label>
                    <p-multiselect
                      class="w-full"
                      [options]="temperatureAnnulusMultiOptions()"
                      [(ngModel)]="options().temperatureChartConfig.annulusOrPipeNames"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all columns"
                      display="chip"
                      [maxSelectedLabels]="3"
                      appendTo="body"
                    ></p-multiselect>
                    <small class="text-gray-600 dark:text-gray-400">Shows how temperature at this radial point varies across operations</small>
                  </div>
                }
                @if (options().temperatureChartConfig.scope === 'SingleOperation') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Operation Names (optional)</label>
                    <p-multiselect
                      class="w-full"
                      [options]="operationMultiOptions()"
                      [(ngModel)]="options().temperatureChartConfig.operationNames"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all operations"
                      display="chip"
                      [maxSelectedLabels]="3"
                      appendTo="body"
                    ></p-multiselect>
                  </div>
                }
              </div>
            }
          </div>

          <p-divider></p-divider>

          <div>
            <h3 class="underline pb-3">Pressure</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includePressurePlot" (ngModelChange)="onOptionChange('includePressurePlot', $event)" [binary]="true" inputId="pressPlot" />
                <label for="pressPlot" class="ml-2">Pressure Plot</label>
              </div>
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includePressureTable" (ngModelChange)="onOptionChange('includePressureTable', $event)" [binary]="true" inputId="pressTable" />
                <label for="pressTable" class="ml-2">Pressure Table</label>
              </div>
            </div>
            @if (options().includePressurePlot || options().includePressureTable) {
              <div class="mt-3 ml-4 space-y-2">
                <div>
                  <label class="block mb-1 text-sm font-medium">Scope</label>
                  <p-select
                    class="w-full"
                    [options]="pressureScopeOptions"
                    [(ngModel)]="options().pressureChartConfig.scope"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                  ></p-select>
                </div>
                @if (options().pressureChartConfig.scope === 'MultipleOperations') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Annulus/Pipe Names (optional)</label>
                    <p-multiselect
                      class="w-full"
                      [options]="pressureAnnulusMultiOptions()"
                      [(ngModel)]="options().pressureChartConfig.annulusOrPipeNames"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all annuli"
                      display="chip"
                      [maxSelectedLabels]="3"
                      appendTo="body"
                    ></p-multiselect>
                    <small class="text-gray-600 dark:text-gray-400">Shows how pressure at this radial point varies across operations</small>
                  </div>
                }
                @if (options().pressureChartConfig.scope === 'SingleOperation') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Operation Names (optional)</label>
                    <p-multiselect
                      class="w-full"
                      [options]="operationMultiOptions()"
                      [(ngModel)]="options().pressureChartConfig.operationNames"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all operations"
                      display="chip"
                      [maxSelectedLabels]="3"
                      appendTo="body"
                    ></p-multiselect>
                  </div>
                }
              </div>
            }
          </div>

          <p-divider></p-divider>

          <div>
            <h3 class="underline pb-3">Annular Results</h3>
            <div class="flex items-center mb-3">
              <p-checkbox [(ngModel)]="includeAnnularResultsEnabled" [binary]="true" inputId="includeAnnularResults" (onChange)="onAnnularResultsToggle($event.checked)" />
              <label for="includeAnnularResults" class="ml-2">Include Annular Results</label>
            </div>
            @if (options().includeAnnularResults) {
              <div class="mt-3 ml-4 space-y-2">
                <div>
                  <label class="block mb-1 text-sm font-medium">Scope</label>
                  <p-select
                    class="w-full"
                    [options]="annularResultsScopeOptions"
                    [(ngModel)]="options().annularResultsChartConfig.scope"
                    optionLabel="label"
                    optionValue="value"
                    appendTo="body"
                  ></p-select>
                </div>
                @if (options().annularResultsChartConfig.scope === 'SingleResult') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Operation Name (optional)</label>
                    <p-select
                      class="w-full"
                      [options]="operationOptions()"
                      [(ngModel)]="options().annularResultsChartConfig.operationName"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all operations"
                      appendTo="body"
                    ></p-select>
                  </div>
                  <div>
                    <label class="block mb-1 text-sm font-medium">Property</label>
                    <p-select
                      class="w-full"
                      [options]="annularResultsPropertyOptions()"
                      [ngModel]="options().annularResultsChartConfig.propertyName || annularResultsPropertyOptions()[0]?.value"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select property"
                      appendTo="body"
                    ></p-select>
                  </div>
                }
                @if (options().annularResultsChartConfig.scope === 'SingleAnnulus') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Operation Name (optional)</label>
                    <p-select
                      class="w-full"
                      [options]="operationOptions()"
                      [(ngModel)]="options().annularResultsChartConfig.operationName"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all operations"
                      appendTo="body"
                    ></p-select>
                  </div>
                  <div>
                    <label class="block mb-1 text-sm font-medium">Annulus Name</label>
                    <p-select
                      class="w-full"
                      [options]="annulusOptions()"
                      [(ngModel)]="options().annularResultsChartConfig.annulusName"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Select annulus"
                      appendTo="body"
                    ></p-select>
                  </div>
                  <div>
                    <label class="block mb-1 text-sm font-medium">Properties (select up to 10)</label>
                    <p-multiselect
                      class="w-full"
                      [options]="annularResultsPropertyOptions()"
                      [(ngModel)]="options().annularResultsChartConfig.selectedProperties"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for default properties"
                      display="chip"
                      [maxSelectedLabels]="3"
                      [selectionLimit]="10"
                      appendTo="body"
                    ></p-multiselect>
                    <small class="text-gray-600 dark:text-gray-400">Leave empty to show default properties (Pressure, Velocity, Density, Viscosity)</small>
                  </div>
                }
              </div>
            }
          </div>

          <p-divider></p-divider>

          <div>
            <h3 class="underline pb-3">Operation Envelope</h3>
            <div class="grid grid-cols-2 gap-2">
              <div class="flex items-center">
                <p-checkbox [ngModel]="options().includeOperationEnvelopePlot" (ngModelChange)="onOptionChange('includeOperationEnvelopePlot', $event)" [binary]="true" inputId="opEnvPlot" />
                <label for="opEnvPlot" class="ml-2">Operation Envelope Plot</label>
              </div>
              @if (options().operationEnvelopeChartConfig.scope !== 'MultipleOperations') {
                <div class="flex items-center">
                  <p-checkbox [ngModel]="options().includeOperationEnvelopeTable" (ngModelChange)="onOptionChange('includeOperationEnvelopeTable', $event)" [binary]="true" inputId="opEnvTable" />
                  <label for="opEnvTable" class="ml-2">Operation Envelope Table</label>
                </div>
              }
            </div>
            @if (options().includeOperationEnvelopePlot || options().includeOperationEnvelopeTable) {
              <div class="mt-3 ml-4 space-y-2">
                <div>
                  <label class="block mb-1 text-sm font-medium">Scope</label>
                  <p-select
                    class="w-full"
                    [options]="operationEnvelopeScopeOptions"
                    [(ngModel)]="options().operationEnvelopeChartConfig.scope"
                    optionLabel="label"
                    optionValue="value"
                    (onChange)="onOperationEnvelopeScopeChange($event.value)"
                    appendTo="body"
                  ></p-select>
                </div>
                @if (options().operationEnvelopeChartConfig.scope === 'SingleOperation') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Operation Name (optional)</label>
                    <p-select
                      class="w-full"
                      [options]="operationOptions()"
                      [(ngModel)]="options().operationEnvelopeChartConfig.operationName"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all operations"
                      appendTo="body"
                    ></p-select>
                  </div>
                }
                @if (options().operationEnvelopeChartConfig.scope === 'SingleOperation' && options().includeOperationEnvelopeTable) {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Table Output</label>
                    <p-select
                      class="w-full"
                      [options]="operationEnvelopeTableOutputOptions"
                      [(ngModel)]="options().operationEnvelopeChartConfig.tableOutput"
                      optionLabel="label"
                      optionValue="value"
                      appendTo="body"
                    ></p-select>
                    <small class="text-gray-600 dark:text-gray-400">Choose what data to include in the table</small>
                  </div>
                }
                @if (options().operationEnvelopeChartConfig.scope === 'MultipleOperations') {
                  <div>
                    <label class="block mb-1 text-sm font-medium">Phase Envelope (optional)</label>
                    <p-select
                      class="w-full"
                      [options]="phaseEnvelopeOptions()"
                      [(ngModel)]="options().operationEnvelopeChartConfig.fluidName"
                      optionLabel="label"
                      optionValue="value"
                      placeholder="Leave empty for all phase envelopes"
                      appendTo="body"
                    ></p-select>
                    <small class="text-gray-600 dark:text-gray-400">Shows how operations relate to the selected phase envelope</small>
                  </div>
                }
              </div>
            }
          </div>
        </div>

        @if (validationErrors().length > 0) {
          <div class="mt-3">
            <p-message severity="error" [text]="validationErrors()[0]"></p-message>
          </div>
        }

      </div>
    </p-scrollPanel>

      <div class="flex justify-end gap-2 mt-4">
        <p-button outlined="true" label="Cancel" class="outlined-button" size="small" severity="danger" (onClick)="cancel()"></p-button>
        <p-button outlined="true" class="outlined-button" size="small" severity="success" label="Save Setup" [disabled]="validationErrors().length > 0" (onClick)="confirm()"></p-button>
      </div>
  </div>
  `,
  standalone: false
})
export class PericalReportOptionsDialogComponent {
  private dialogRef = inject(DynamicDialogRef);
  private config = inject(DynamicDialogConfig);

  public options = signal<PericalDialogOptions>(this.getInitialOptions());
  public validationErrors = computed(() => validateReportOptions(this.options()));
  public selectedPreset: PresetOption | null = null;

  get includeAnnularResultsEnabled(): boolean {
    return this.options().includeAnnularResults || false;
  }

  set includeAnnularResultsEnabled(value: boolean) {
    // Setter needed for ngModel binding, actual update happens in onAnnularResultsToggle
  }

  private static readonly TUBULAR_TYPES = ['Casing', 'Tieback', 'Liner', 'Tubing', 'DrillPipe', 'Riser'];

  private getHeadersData(): any {
    const headersData = this.config.data?.headersData ?? {};
    const keysToTruncate = [
      'temperatureAnnulusPipeNames', 'TemperatureAnnulusPipeNames',
      'temperatureAnnulusOrPipeNames', 'TemperatureAnnulusOrPipeNames',
      'pressureAnnulusPipeNames', 'PressureAnnulusPipeNames',
      'pressureAnnulusOrPipeNames', 'PressureAnnulusOrPipeNames',
      'annulusOrPipeNames', 'AnnulusOrPipeNames',
      'annulusNames', 'AnnulusNames'
    ];

    for (const key of keysToTruncate) {
      if (Array.isArray(headersData[key])) {
        headersData[key] = headersData[key].map((name: string) =>
          typeof name === 'string' ? this.truncateTubularName(name) : name
        );
      }
    }

    return headersData;
  }

  private truncateTubularName(name: string): string {
    let typeIndex = -1;
    for (const type of PericalReportOptionsDialogComponent.TUBULAR_TYPES) {
      const index = name.toLowerCase().indexOf(type.toLowerCase());
      if (index >= 0) {
        typeIndex = index;
        break;
      }
    }

    if (typeIndex === -1) {
      return name;
    }

    const sizePortion = name.substring(0, typeIndex).trimEnd();
    const suffix = name.substring(typeIndex);
    const sections = sizePortion.split(' x ').map(s => s.trim()).filter(s => s.length > 0);

    if (sections.length <= 3) {
      return name;
    }

    const truncated = sections.slice(0, 3).join(' x ');
    return `${truncated} x ... ${suffix}`;
  }

  private coerceStringArray(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => {
        if (typeof item === 'string') {
          return item;
        }

        if (item && typeof item === 'object') {
          const candidate = item as Record<string, unknown>;
          const objectValue =
            candidate['name'] ??
            candidate['Name'] ??
            candidate['value'] ??
            candidate['Value'] ??
            candidate['label'] ??
            candidate['Label'];

          return typeof objectValue === 'string' ? objectValue : '';
        }

        return '';
      })
      .filter((item): item is string => !!item && item.trim().length > 0);
  }

  private getHeaderList(...keys: string[]): string[] {
    const headersData = this.getHeadersData();

    for (const key of keys) {
      const list = this.coerceStringArray(headersData[key]);
      if (list.length > 0) {
        return list;
      }
    }

    return [];
  }

  // Map operations to dropdown options
  public operationOptions = computed(() => {
    const operationNames = this.getHeaderList('operationNames', 'OperationNames');
    if (operationNames.length === 0) return [{ label: 'Leave empty for all operations', value: '' }];
    return [
      { label: 'Leave empty for all operations', value: '' },
      ...operationNames.map((name: string) => ({
        label: name,
        value: name
      }))
    ];
  });

  // Operations list for multi-select controls
  public operationMultiOptions = computed(() => {
    const operationNames = this.getHeaderList('operationNames', 'OperationNames');
    if (operationNames.length === 0) return [];
    return operationNames.map((name: string) => ({
      label: name,
      value: name
    }));
  });

  // Temperature annulus/pipe options
  public temperatureAnnulusOptions = computed(() => {
    const annulusNames = this.getHeaderList(
      'temperatureAnnulusPipeNames',
      'TemperatureAnnulusPipeNames',
      'temperatureAnnulusOrPipeNames',
      'TemperatureAnnulusOrPipeNames',
      'annulusOrPipeNames',
      'AnnulusOrPipeNames',
      'annulusNames',
      'AnnulusNames'
    );

    if (annulusNames.length === 0) return [{ label: 'Leave empty for all annuli', value: '' }];
    return [
      { label: 'Leave empty for all annuli', value: '' },
      ...annulusNames.map((name: string) => ({
        label: name,
        value: name
      }))
    ];
  });

  public temperatureAnnulusMultiOptions = computed(() => {
    const annulusNames = this.getHeaderList(
      'temperatureAnnulusPipeNames',
      'TemperatureAnnulusPipeNames',
      'temperatureAnnulusOrPipeNames',
      'TemperatureAnnulusOrPipeNames',
      'annulusOrPipeNames',
      'AnnulusOrPipeNames',
      'annulusNames',
      'AnnulusNames'
    );

    if (annulusNames.length === 0) return [];
    return annulusNames.map((name: string) => ({
      label: name,
      value: name
    }));
  });

  // Pressure annulus/pipe options
  public pressureAnnulusOptions = computed(() => {
    const annulusNames = this.getHeaderList(
      'pressureAnnulusPipeNames',
      'PressureAnnulusPipeNames',
      'pressureAnnulusOrPipeNames',
      'PressureAnnulusOrPipeNames',
      'annulusOrPipeNames',
      'AnnulusOrPipeNames',
      'annulusNames',
      'AnnulusNames'
    );

    if (annulusNames.length === 0) return [{ label: 'Leave empty for all annuli', value: '' }];
    return [
      { label: 'Leave empty for all annuli', value: '' },
      ...annulusNames.map((name: string) => ({
        label: name,
        value: name
      }))
    ];
  });

  public pressureAnnulusMultiOptions = computed(() => {
    const annulusNames = this.getHeaderList(
      'pressureAnnulusPipeNames',
      'PressureAnnulusPipeNames',
      'pressureAnnulusOrPipeNames',
      'PressureAnnulusOrPipeNames',
      'annulusOrPipeNames',
      'AnnulusOrPipeNames',
      'annulusNames',
      'AnnulusNames'
    );

    if (annulusNames.length === 0) return [];
    return annulusNames.map((name: string) => ({
      label: name,
      value: name
    }));
  });

  // Annular results annulus options
  public annulusOptions = computed(() => {
    const annulusNames = this.getHeaderList(
      'annulusNames',
      'AnnulusNames',
      'annulusOrPipeNames',
      'AnnulusOrPipeNames',
      'temperatureAnnulusPipeNames',
      'TemperatureAnnulusPipeNames',
      'pressureAnnulusPipeNames',
      'PressureAnnulusPipeNames'
    );

    if (annulusNames.length === 0) {
      return [{ label: 'No annuli available', value: '' }];
    }
    return annulusNames.map((name: string) => ({
      label: name,
      value: name
    }));
  });

  // Phase envelope options for multiple operations
  public phaseEnvelopeOptions = computed(() => {
    const headersData = this.getHeadersData();
    if (!headersData?.phaseEnvelopeOptions || headersData.phaseEnvelopeOptions.length === 0) {
      return [{ label: 'Leave empty for all phase envelopes', value: '' }];
    }
    return [
      { label: 'Leave empty for all phase envelopes', value: '' },
      ...headersData.phaseEnvelopeOptions.map((name: string) => ({
        label: name,
        value: name
      }))
    ];
  });

  public presets: PresetOption[] = [
    { label: 'None (No Options Selected)', value: 'none', options: PericalReportPresets.None },
    { label: 'Minimal (Essential Charts)', value: 'minimal', options: PericalReportPresets.Minimal },
    { label: 'All Options', value: 'default', options: PericalReportPresets.Default },
    { label: 'Charts Only', value: 'charts', options: PericalReportPresets.ChartsOnly },
    { label: 'Tables Only', value: 'tables', options: PericalReportPresets.TablesOnly }
  ];

  public temperatureScopeOptions = [
    { label: 'Single', value: TemperatureScope.SingleOperation },
    { label: 'Multiple', value: TemperatureScope.MultipleOperations }
  ];

  public pressureScopeOptions = [
    { label: 'Single', value: PressureScope.SingleOperation },
    { label: 'Multiple', value: PressureScope.MultipleOperations }
  ];

  public annularResultsScopeOptions = [
    { label: 'Single Result (Chart)', value: AnnularResultsScope.SingleResult },
    { label: 'Single Annulus (Table)', value: AnnularResultsScope.SingleAnnulus }
  ];

  // Fluid/Mixture properties (original list)
  private fluidMixtureProperties = [
    { label: 'Pressure', value: 'Pressure' },
    { label: 'Fluid Velocity', value: 'FluidVelocity' },
    { label: 'Fluid Density', value: 'FluidDensity' },
    { label: 'Fluid Viscosity', value: 'FluidViscosity' },
    { label: 'Fluid Yield Point', value: 'FluidYieldPoint' },
    { label: 'Fluid Flow Regime', value: 'FluidFlowRegime' },
    { label: 'Fluid Quality', value: 'FluidQuality' },
    { label: 'Fluid Holdup', value: 'FluidHoldup' },
    { label: 'Vaporization Enthalpy', value: 'VaporizationEnthalpy' },
    { label: 'Heat Generation', value: 'HeatGeneration' },
    { label: 'Latent Heat Term', value: 'LatentHeatTerm' },
    { label: 'Fluid Pressure Loss', value: 'FluidPressureLoss' },
    { label: 'Frictional Pressure Loss', value: 'FrictionalPressureLoss' },
    { label: 'Accelerational Pressure Loss', value: 'AccelerationalPressureLoss' },
    { label: 'Fluid Reynolds Number', value: 'FluidReynoldsNumber' },
    { label: 'Fluid Fanning Friction Factor', value: 'FluidFanningFrictionFactor' },
    { label: 'Fluid Prandtl Number', value: 'FluidPrandtlNumber' },
    { label: 'Fluid Convection Coefficient', value: 'FluidConvectionCoefficient' },
    { label: 'Fluid Natural Convection Coefficient', value: 'FluidNaturalConvectionCoefficient' },
    { label: 'Fluid Radiation Coefficient', value: 'FluidRadiationCoefficient' },
    { label: 'Fluid Nusselt Number', value: 'FluidNusseltNumber' },
    { label: 'Fluid Grashof Number', value: 'FluidGrashofNumber' },
    { label: 'Fluid Specific Heat Capacity Cp', value: 'FluidSpecificHeatCapacityCp' },
    { label: 'Fluid Specific Heat Capacity Cv', value: 'FluidSpecificHeatCapacityCv' },
    { label: 'Fluid Effective Conductivity', value: 'FluidEffectiveConductivity' },
    { label: 'Fluid Expansivity', value: 'FluidExpansivity' },
    { label: 'Liquid Velocity', value: 'LiquidVelocity' },
    { label: 'Liquid Density', value: 'LiquidDensity' },
    { label: 'Liquid Plastic Viscosity', value: 'LiquidPlasticViscosity' },
    { label: 'Liquid Specific Heat Cp', value: 'LiquidSpecificHeatCp' },
    { label: 'Liquid Specific Heat Cv', value: 'LiquidSpecificHeatCv' },
    { label: 'Liquid Thermal Conductivity', value: 'LiquidThermalConductivity' },
    { label: 'Liquid Expansivity', value: 'LiquidExpansivity' },
    { label: 'Liquid Dissolved GOR', value: 'LiquidDissolvedGor' },
    { label: 'Liquid Water Holdup', value: 'LiquidWaterHoldup' },
    { label: 'Liquid Water Fraction Density', value: 'LiquidWaterFractionDensity' },
    { label: 'Gas Velocity', value: 'GasVelocity' },
    { label: 'Gas Density', value: 'GasDensity' },
    { label: 'Gas Plastic Viscosity', value: 'GasPlasticViscosity' },
    { label: 'Gas Specific Heat Cp', value: 'GasSpecificHeatCp' },
    { label: 'Gas Specific Heat Cv', value: 'GasSpecificHeatCv' },
    { label: 'Gas Thermal Conductivity', value: 'GasThermalConductivity' },
    { label: 'Gas Expansivity', value: 'GasExpansivity' }
  ];

  // Liquid & Gas Fractions properties
  private liquidGasFractionProperties = [
    { label: 'Liquid Velocity', value: 'LiquidVelocity' },
    { label: 'Liquid Density', value: 'LiquidDensity' },
    { label: 'Liquid Viscosity', value: 'LiquidViscosity' },
    { label: 'Liquid Specific Heat Cp', value: 'LiquidSpecificHeatCp' },
    { label: 'Liquid Specific Heat Cv', value: 'LiquidSpecificHeatCv' },
    { label: 'Liquid Thermal Conductivity', value: 'LiquidThermalConductivity' },
    { label: 'Liquid Expansion Coeff', value: 'LiquidExpansionCoeff' },
    { label: 'Liquid Dissolved GOR', value: 'LiquidDissolvedGor' },
    { label: 'Liquid Water Holdup', value: 'LiquidWaterHoldup' },
    { label: 'Liquid Water Fraction Density', value: 'LiquidWaterFractionDensity' },
    { label: 'Gas Velocity', value: 'GasVelocity' },
    { label: 'Gas Density', value: 'GasDensity' },
    { label: 'Gas Viscosity', value: 'GasViscosity' },
    { label: 'Gas Specific Heat Cp', value: 'GasSpecificHeatCp' },
    { label: 'Gas Specific Heat Cv', value: 'GasSpecificHeatCv' },
    { label: 'Gas Thermal Conductivity', value: 'GasThermalConductivity' },
    { label: 'Gas Expansion Coeff', value: 'GasExpansionCoeff' }
  ];

  // Computed property that returns the appropriate property list based on fluid/gas selection
  public annularResultsPropertyOptions = computed(() => {
    const fluidOrGas = this.options().annularResultsChartConfig?.fluidOrGasFractions;
    return fluidOrGas === 'Gas' ? this.liquidGasFractionProperties : this.fluidMixtureProperties;
  });

  public fluidGasDisplay = [
    { label: 'Fluid/Mixture', value: 'Fluid' },
    { label: 'Liquid & Gas Fractions', value: 'Gas' }
  ];

  public operationEnvelopeScopeOptions = [
    { label: 'Single Operation', value: OperationEnvelopeScope.SingleOperation },
    { label: 'Multiple Operations', value: OperationEnvelopeScope.MultipleOperations }
  ];

  public operationEnvelopeTableOutputOptions = [
    { label: 'Operation Profile', value: OperationEnvelopeTableOutput.OperationProfile },
    { label: 'Phase Envelope', value: OperationEnvelopeTableOutput.PhaseEnvelope },
    { label: 'Both', value: OperationEnvelopeTableOutput.Both }
  ];

  constructor() {
    // Scope determines output type (SingleResult = Chart, SingleAnnulus = Table)
    // Backend handles this automatically based on scope
  }

  public onAnnularResultsToggle(checked: boolean): void {
    if (checked) {
      // When enabling, default to Single Result (chart)
      this.options.update(opts => ({
        ...opts,
        includeAnnularResults: true,
        annularResultsChartConfig: {
          ...opts.annularResultsChartConfig,
          scope: 'SingleResult' as AnnularResultsScope
        }
      }));
    } else {
      // When disabling
      this.options.update(opts => ({
        ...opts,
        includeAnnularResults: false
      }));
    }
  }

  public onOperationEnvelopeScopeChange(scope: OperationEnvelopeScope): void {
    // When switching to Multiple Operations, disable table output
    if (scope === OperationEnvelopeScope.MultipleOperations) {
      this.options.update(opts => ({
        ...opts,
        includeOperationEnvelopeTable: false
      }));
    }
  }

  private getInitialOptions(): PericalDialogOptions {
    const initialOptions = this.config.data?.initialOptions as PericalReportOptions | null | undefined;
    if (initialOptions) {
      return this.cloneOptions(initialOptions);
    }

    const preset = this.config.data?.preset || 'none';
    switch (preset) {
      case 'none':
        return this.cloneOptions(PericalReportPresets.None);
      case 'minimal':
        return this.cloneOptions(PericalReportPresets.Minimal);
      case 'charts':
        return this.cloneOptions(PericalReportPresets.ChartsOnly);
      case 'tables':
        return this.cloneOptions(PericalReportPresets.TablesOnly);
      case 'default':
        return this.cloneOptions(PericalReportPresets.Default);
      default:
        return this.cloneOptions(PericalReportPresets.None);
    }
  }

  private cloneOptions(options: PericalReportOptions): PericalDialogOptions {
    const clonedOptions = JSON.parse(JSON.stringify(options)) as PericalReportOptions;
    const temperatureChartConfig: TemperatureChartConfig = {
      ...JSON.parse(JSON.stringify(DEFAULT_CHART_CONFIGS.temperature)),
      ...(clonedOptions.temperatureChartConfig ?? {}),
      operationNames: clonedOptions.temperatureChartConfig?.operationNames
        ?? (clonedOptions.temperatureChartConfig?.operationName ? [clonedOptions.temperatureChartConfig.operationName] : []),
      annulusOrPipeNames: clonedOptions.temperatureChartConfig?.annulusOrPipeNames
        ?? (clonedOptions.temperatureChartConfig?.annulusOrPipeName ? [clonedOptions.temperatureChartConfig.annulusOrPipeName] : [])
    };

    const pressureChartConfig: PressureChartConfig = {
      ...JSON.parse(JSON.stringify(DEFAULT_CHART_CONFIGS.pressure)),
      ...(clonedOptions.pressureChartConfig ?? {}),
      operationNames: clonedOptions.pressureChartConfig?.operationNames
        ?? (clonedOptions.pressureChartConfig?.operationName ? [clonedOptions.pressureChartConfig.operationName] : []),
      annulusOrPipeNames: clonedOptions.pressureChartConfig?.annulusOrPipeNames
        ?? (clonedOptions.pressureChartConfig?.annulusOrPipeName ? [clonedOptions.pressureChartConfig.annulusOrPipeName] : [])
    };

    return {
      ...clonedOptions,
      temperatureChartConfig,
      pressureChartConfig,
      annularResultsChartConfig: clonedOptions.annularResultsChartConfig ?? JSON.parse(JSON.stringify(DEFAULT_CHART_CONFIGS.annularResults)),
      operationEnvelopeChartConfig: clonedOptions.operationEnvelopeChartConfig ?? JSON.parse(JSON.stringify(DEFAULT_CHART_CONFIGS.operationEnvelope))
    };
  }

  public applyPreset(preset: PresetOption): void {
    this.options.set(this.cloneOptions(preset.options));
  }

  public confirm(): void {
    if (this.validationErrors().length === 0) {
      this.dialogRef.close(this.buildOutputOptions());
    }
  }

  private buildOutputOptions(): PericalDialogOptions {
    const currentOptions = this.options();

    const temperatureOperationNames = currentOptions.temperatureChartConfig.operationNames ?? [];
    const temperatureAnnulusNames = currentOptions.temperatureChartConfig.annulusOrPipeNames ?? [];
    const pressureOperationNames = currentOptions.pressureChartConfig.operationNames ?? [];
    const pressureAnnulusNames = currentOptions.pressureChartConfig.annulusOrPipeNames ?? [];

    return {
      ...currentOptions,
      temperatureChartConfig: {
        ...currentOptions.temperatureChartConfig,
        operationName: temperatureOperationNames.length === 1 ? temperatureOperationNames[0] : null,
        annulusOrPipeName: temperatureAnnulusNames.length === 1 ? temperatureAnnulusNames[0] : null,
        operationNames: temperatureOperationNames,
        annulusOrPipeNames: temperatureAnnulusNames
      },
      pressureChartConfig: {
        ...currentOptions.pressureChartConfig,
        operationName: pressureOperationNames.length === 1 ? pressureOperationNames[0] : null,
        annulusOrPipeName: pressureAnnulusNames.length === 1 ? pressureAnnulusNames[0] : null,
        operationNames: pressureOperationNames,
        annulusOrPipeNames: pressureAnnulusNames
      }
    };
  }

  public onOptionChange(field: keyof PericalReportOptions, value: boolean): void {
    this.options.update(opts => ({
      ...opts,
      [field]: value
    }));
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }
}
